'use client';
import React from 'react';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import type { DiagnosisPhase } from './AIDiagnosisContent';

interface DiagnosisPhaseStreamProps {
  phases: DiagnosisPhase[];
}

export default function DiagnosisPhaseStream({ phases }: DiagnosisPhaseStreamProps) {
  return (
    <div
      style={{
        backgroundColor: '#0d1117',
        border: '1px solid #1e293b',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '20px',
        }}
      >
        <div
          className="font-mono"
          style={{
            fontSize: '10px',
            fontWeight: 600,
            color: '#8b5cf6',
            backgroundColor: 'rgba(139,92,246,0.1)',
            border: '1px solid rgba(139,92,246,0.25)',
            padding: '3px 8px',
            borderRadius: '4px',
            letterSpacing: '0.08em',
          }}
        >
          CLAUDE AI PIPELINE
        </div>
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          {phases.filter((p) => p.status === 'done').length} / {phases.length} phases complete
        </span>
      </div>

      <div style={{ position: 'relative' }}>
        {/* Vertical line */}
        <div
          style={{
            position: 'absolute',
            left: '15px',
            top: '16px',
            bottom: '16px',
            width: '1px',
            backgroundColor: '#1e293b',
          }}
        />

        {phases.map((phase, idx) => {
          const isRunning = phase.status === 'running';
          const isDone = phase.status === 'done';
          const isWaiting = phase.status === 'waiting';

          return (
            <div
              key={phase.id}
              style={{
                display: 'flex',
                gap: '16px',
                marginBottom: idx < phases.length - 1 ? '20px' : '0',
                position: 'relative',
                opacity: isWaiting ? 0.4 : 1,
                transition: 'opacity 300ms ease',
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: isDone
                    ? 'rgba(16,185,129,0.15)'
                    : isRunning
                    ? 'rgba(139,92,246,0.15)'
                    : '#161b22',
                  border: isDone
                    ? '1px solid rgba(16,185,129,0.4)'
                    : isRunning
                    ? '1px solid rgba(139,92,246,0.4)'
                    : '1px solid #2d3748',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  zIndex: 1,
                  transition: 'all 300ms ease',
                }}
              >
                {isDone ? (
                  <CheckCircle2 size={14} style={{ color: '#10b981' }} />
                ) : isRunning ? (
                  <Loader2 size={14} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Circle size={14} style={{ color: '#374151' }} />
                )}
              </div>

              {/* Content */}
              <div style={{ flex: 1, paddingTop: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <span
                    className="font-mono"
                    style={{
                      fontSize: '9px',
                      color: '#374151',
                      letterSpacing: '0.1em',
                    }}
                  >
                    PHASE {phase.step}
                  </span>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: isDone ? '#e2e8f0' : isRunning ? '#c4b5fd' : '#64748b',
                    }}
                  >
                    {phase.label}
                  </span>
                  {isDone && phase.duration && (
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '10px',
                        color: '#10b981',
                        backgroundColor: 'rgba(16,185,129,0.08)',
                        padding: '1px 6px',
                        borderRadius: '3px',
                      }}
                    >
                      {phase.duration}ms
                    </span>
                  )}
                  {isRunning && (
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '10px',
                        color: '#8b5cf6',
                        animation: 'pulse 1.5s infinite',
                      }}
                    >
                      processing...
                    </span>
                  )}
                </div>
                {(isDone || isRunning) && (
                  <p
                    className="font-mono"
                    style={{
                      margin: 0,
                      fontSize: '11px',
                      color: '#64748b',
                      lineHeight: 1.5,
                    }}
                  >
                    {phase.detail}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}